import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {StromcheckAnfrage} from "../../anfrage/stromcheck.anfrage";
import {StromcheckRequestService} from "../../services/request.service";

@Component({
  selector: 'app-e-mobility-box',
  templateUrl: './e-mobility-box.component.html',
})

export class EMobilityBoxComponent implements OnInit {
  /**
   * form for validation
   */
  form: UntypedFormGroup;

  /**
   * placeholder for different values will be set
   */
  placeholder: {};

  /**
   * will be set to true if there is an input amount of car electricity
   */
  hasCarElectricity: boolean = false;

  /**
   * valid amount of car electricity
   */
  validCarElectricityValue: number = 0;

  /**
   * textkey prefix
   */
  textKeyPrefix: string = 'stromcheck.ergebnis.emobility';

  /**
   * get Stromcheckanfrage
   */
  @Input() anfrage: StromcheckAnfrage;

  /**
   * Event emitter activated every time when class changes after car electricity input
   */
  @Output() onClassChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Event emitter activated every time when car electricity input changes
   */
  @Output() onCarElectricityChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * textkey will be set if there is a form error
   */
  formError: string = null;

  /**
   * set placeholder for form error
   */
  formErrorPlaceholder: {[key: string]: any} = {};

  constructor(private requestService: StromcheckRequestService) {
  }

  ngOnInit() {
    //create form
    this.form = new UntypedFormGroup({
      'has_electric_car': new UntypedFormControl(this.anfrage.userInput.elektrofahrzeug_vorhanden, []),
      'car_electricity_unknown': new UntypedFormControl(this.anfrage.userInput.ladestrom_unbekannt, []),
      'car_electricity': new UntypedFormControl(this.anfrage.userInput.ladestrom_elektrofahrzeug_verbrauch, [Validators.required, Validators.min(1)])
    });

    // save elektrofahrzeug_vorhanden also if set to false
    this.form.controls['has_electric_car'].valueChanges.subscribe(() => this.saveCarElectricity());
    this.form.controls['car_electricity_unknown'].valueChanges.subscribe(() => this.saveCarElectricity());

    // save input of car electricity if > 0
    this.hasCarElectricity = this.anfrage.userInput.ladestrom_elektrofahrzeug_verbrauch > 0;
    // get placeholder car_electricity from backend
    this.placeholder = this.anfrage?.result?.placeholder ?? {};
  }

  /**
   * save input value to car electricity amount, send to backend and get new total consumption, scroll up to new result if class changes
   */
  saveCarElectricity(validateCarElectricity: boolean = false){
    this.formError = null;

    // if no electric car set car_electricity to null and car_electricity_unknown to false
    if(this.form?.controls['has_electric_car']?.value === false) {
      this.form.controls['car_electricity'].setValue(null, {emitEvent: false});
      this.form.controls['car_electricity_unknown'].setValue(false, {emitEvent: false});
    }
    // if car electricity is unknown set car_electricity to null
    if(this.form?.controls['car_electricity_unknown']?.value === true) {
      this.form.controls['car_electricity'].setValue(null, {emitEvent: false});
    }

    //set form control values to user input values
    this.anfrage.userInput.ladestrom_unbekannt = this.form?.controls['car_electricity_unknown']?.value;
    this.anfrage.userInput.elektrofahrzeug_vorhanden = this.form?.controls['has_electric_car']?.value;
    this.anfrage.userInput.ladestrom_elektrofahrzeug_verbrauch = this.form?.controls['car_electricity']?.value;

    //save in browser
    this.requestService.setAnfrage(this.anfrage);

    //save current class
    const currentClass = this.anfrage?.result?.data?.energieeffizienzklasse;

    //send data to backend and update result
    this.requestService.backendService.getRequestId().subscribe(requestId => {
      // calculate result
      this.requestService.backendService.result(this.anfrage, requestId).subscribe(data => {
        this.anfrage.result = data;
        this.requestService.setAnfrage(this.anfrage);

        //show info box if hasCarElectricity is true and change toast on ergebnis component
        this.hasCarElectricity = this.form?.controls['car_electricity']?.valid;

        //generate form error if user input to car electricity is not valid
        if(validateCarElectricity === true && this.form?.controls['has_electric_car']?.value === true && (this.form?.controls['car_electricity_unknown']?.value === false || this.form?.controls['car_electricity_unknown']?.value === null) && this.form?.controls['car_electricity']?.valid === false) {
          this.formError = "invalid_car_electricity";
          this.hasCarElectricity = false;
          return;
        }

        // remember the valid car electricity value
        this.validCarElectricityValue = this.anfrage.userInput.ladestrom_elektrofahrzeug_verbrauch;

        //save input value of car electricity input
        this.placeholder = {...this.anfrage.result.placeholder};

        //scroll up if class and consumption have changed
       this.onClassChange.emit(currentClass.toUpperCase() !== data.data.energieeffizienzklasse.toUpperCase());

       //update savings if car electricity amount changes
       this.onCarElectricityChange.emit(!this.form.controls['car_electricity'].pristine);

      }, error => {
        if (error?.status === 400) {
          // show error message to the user if input was not valid
          this.formError = error.error?.result?.status?.errors[0]?.code;
          this.formErrorPlaceholder = error.error?.result?.status?.errors[0]?.placeholders;

          // reset the old (valid) car elecitricity value
          this.anfrage.userInput.ladestrom_elektrofahrzeug_verbrauch = this.validCarElectricityValue;
          this.requestService.setAnfrage(this.anfrage);

          //get error message from backend
          this.hasCarElectricity = false;
          console.error("Could not get data from backend", error);
        }
        else {
          // generic error, mostly on the connection to the backend.
          this.formError = "connection";
          console.error("Could not get data from backend", error);
        }
      });
    }, error => {
      // show error message with internal error
      this.formError = "internal";
      // an error occured while talking to the backend.
      console.error("Could not get requestid from backend", error);
    });
  }
}
